import type { FC } from 'react'
import { useState } from 'react'

import { Button } from '@skytech/ui/Button'
import { useExpandableText } from '@skytech/utils'
import { sanitizeProductDescription } from '@skytech/utils/html-sanitization'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useShopNewOrderContext } from '@/modules/ecom-policy/shop/new-order/ShopNewOrder.context'
import type { Product as ProductType } from '@/modules/ecom-policy/types'

import Product from './Product'

import './product-group-overview.scss'

const ProductGroupOverview: FC<{
  selectedGroupProductId?: number
  description: string | null
  groupName: string
  groupedProducts: ProductType[]
  onModalClose: () => void
}> = ({ selectedGroupProductId, description, groupedProducts, groupName, onModalClose }) => {
  // Max one product might be selected from a group
  const [groupSelectedProductId, setGroupSelectedProductId] = useState<number | null>(
    selectedGroupProductId || null
  )
  const newOrderContext = useShopNewOrderContext()
  const [selectedProductIds, setSelectedProductIds] = newOrderContext.selectedProductIds
  const [productQuantityById, setProductQuantityById] = newOrderContext.productQuantityById

  const { textRef, isTextExpandable, isFullTextVisible, onFullTextVisibleSet } =
    useExpandableText(120)

  const handleProductSelect = (id: number) => {
    if (!groupSelectedProductId) {
      setSelectedProductIds([...(selectedProductIds || []), id])
      setGroupSelectedProductId(id)
      setProductQuantityById({ ...productQuantityById, [id]: 1 })
      onModalClose()
      return
    }

    if (groupSelectedProductId === id) {
      const filteredSelectedProductIds = selectedProductIds?.filter(
        selectedProductIdItem => selectedProductIdItem !== id
      )

      setSelectedProductIds(filteredSelectedProductIds || null)
      setGroupSelectedProductId(null)
      setProductQuantityById({ ...productQuantityById, [id]: 0 })

      return
    }

    const filteredSelectedProductIds =
      selectedProductIds?.filter(
        selectedProductIdItem => selectedProductIdItem !== groupSelectedProductId
      ) || []

    setSelectedProductIds([...filteredSelectedProductIds, id])
    setProductQuantityById({ ...productQuantityById, [id]: 1 })
    setGroupSelectedProductId(id)
  }

  return (
    <div className="product-overview">
      <header>
        <h3> {groupName}</h3>
      </header>

      {description && description.length >= 10 && (
        <div
          ref={textRef}
          className={twJoin(
            'description',
            isFullTextVisible && 'is-full-desc-visible',
            isTextExpandable && 'is-desc-expandable'
          )}
        >
          <div dangerouslySetInnerHTML={{ __html: sanitizeProductDescription(description) }} />
        </div>
      )}

      <div className={twJoin('desc-toggle-wrapper', isTextExpandable && 'is-desc-expandable')}>
        <button onClick={onFullTextVisibleSet}>
          <Translation
            id={isFullTextVisible ? 'Les mindre...' : 'Les mer...'}
            className="lower-case"
          />
        </button>
      </div>

      <div className="product-overview-items">
        {groupedProducts.map(product => {
          const { id, isMandatory } = product

          // Products marked mandatory should not be visible in product selection
          if (isMandatory) return null

          return (
            <Product
              key={id}
              {...product}
              selectedProductId={groupSelectedProductId}
              quantity={productQuantityById[id] || 0}
              onSelectProduct={() => handleProductSelect(id)}
              onChangeQuantity={quantity =>
                setProductQuantityById({ ...productQuantityById, [id]: quantity })
              }
            />
          )
        })}
      </div>

      <footer>
        <Button variant="tertiary" onClick={onModalClose}>
          <Translation id="Close" />
        </Button>
      </footer>
    </div>
  )
}
export default ProductGroupOverview
